import { useCallback, useEffect, useState } from 'react'
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  ResponsiveContainer,
  Text as RechartsText,
} from 'recharts'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'

import { Select, Text } from '@/ui'
import { Expense, fetchExpensesBreakdown } from '@/actions/reportActions'
import { BRIGHT_COLORS } from '@/constants/colorConstants'
import { YEAR_OPTIONS } from '@/constants/businessConstants'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { ExpensesBreakdownCard } from './ExpensesBreakdownCard'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import { Fonts } from '../../../styles/theme'

const RADIAN = Math.PI / 180
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: {
  cx: number
  cy: number
  midAngle: number
  innerRadius: number
  outerRadius: number
  percent: number
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <RechartsText
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={Fonts.bodyMd}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </RechartsText>
  )
}

const renderActiveShape = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  name,
}: PieSectorDataItem) => (
  <g>
    <RechartsText
      x={cx}
      y={cy}
      dy={8}
      width={100}
      textAnchor="middle"
      verticalAnchor="middle"
      fill={fill}
      style={Fonts.bodyMd}
    >
      {name}
    </RechartsText>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius ? outerRadius + 6 : undefined}
      outerRadius={outerRadius ? outerRadius + 10 : undefined}
      fill={fill}
    />
  </g>
)

export type ActivePayload = {
  percent: number
  value: number
}

type FormattedExpense = {
  name: string
  value: number
  transactionCategoryId: number
}

export const ExpensesPieChartRow = () => {
  const dispatch = useAppDispatch()

  const [data, setData] = useState<FormattedExpense[]>([])
  const [activeIndex, setActiveIndex] = useState<number>()
  const [activePayload, setActivePayload] = useState<ActivePayload | null>(null)
  const [activeTransactionCategoryId, setActiveTransactionCategoryId] =
    useState<number | null>(null)
  const [year, setYear] = useState('all')

  const categories = useReselector(getAllTransactionCategories)

  useEffect(() => {
    // Reset category when year changes
    setActiveTransactionCategoryId(null)
  }, [year])

  const formatData = useCallback(
    (results: Expense[]) => {
      const formattedResult: FormattedExpense[] = []
      results.forEach(({ sum, transactionCategoryId }) => {
        /* 
        We only want to format and show the data in the pie charge if the expense is less than 0 
        this is because there may be causes where the expenses total is actually positive (so a refund),
        in which case, does not make sense to show visually here 
      */
        const sumValue = Number(sum)
        if (sumValue < 0) {
          formattedResult.push({
            name: categories[transactionCategoryId]?.name,
            value: Math.abs(sumValue),
            transactionCategoryId,
          })
        }
      })

      return formattedResult
    },
    [categories]
  )

  useEffect(() => {
    fetchExpensesBreakdown({ year })(dispatch).then(({ expenses }) =>
      setData(formatData(expenses))
    )
  }, [year, dispatch, formatData])

  // The `data` param from recharts is a combination of various SVG properties (cx, cy, fill,
  // angle etc.) in addition to what we provide as data/payload for the values of the pie chart, so
  // this type for `data` isn't exactly the entire object type but a stripped down version for our
  // own purposes.
  //
  // If more properties are needed from the `data` param, suggestion would be to inspect the `data`
  // param (i.e. console.log and view in browser) to see what properties are supplied by recharts
  // on a MouseDown event and add to the `data` type accordingly.
  const onMouseDown = (
    data: {
      payload: { value: number }
      percent: number
      transactionCategoryId: number
    },
    index: number
  ) => {
    const { payload, percent, transactionCategoryId } = data
    const activePayload = {
      ...payload,
      percent,
    }

    setActiveIndex(index)
    setActiveTransactionCategoryId(transactionCategoryId)
    setActivePayload(activePayload)
  }

  return (
    <div className="flex flex-row md:flex-col gap-4">
      <div className=" flex items-center">
        <Text variant={TypographyStyles.H2} className="w-2/3 mr-4">
          Breakdown for:
        </Text>
        <div className="w-1/3">
          <Select
            options={YEAR_OPTIONS}
            onValueChange={(val) => setYear(val ?? 'all')}
            value={year}
            variant="text"
          />
        </div>
      </div>
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-start sm:space-x-6">
        {/* Pie Chart */}
        <div className="w-full sm:w-1/2">
          <ResponsiveContainer width="99%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx={200}
                cy={200}
                isAnimationActive
                labelLine={false}
                label={renderCustomizedLabel}
                innerRadius={80}
                outerRadius={140}
                activeShape={renderActiveShape}
                activeIndex={activeIndex}
                fill="#8884d8"
                dataKey="value"
                onMouseDown={onMouseDown}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      BRIGHT_COLORS[
                        entry.transactionCategoryId % BRIGHT_COLORS.length
                      ]
                    }
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Breakdown Card */}
        <div className="w-full sm:w-1/2">
          <ExpensesBreakdownCard
            activePayload={activePayload}
            activeTransactionCategoryId={activeTransactionCategoryId}
            year={year}
          />
        </div>
      </div>
    </div>
  )
}
